/** What a ticker actually IS, in one plain sentence.
 *
 * Alan, 2026-08-28: "we should be able to hover over or click to get an
 * understanding of what a given instrument is."
 *
 * The firm names instruments constantly — a desk plan says "XLB", a directive
 * says "no-touch XLRE, XLP", a trade story says "SIVR" — and nothing anywhere
 * says what any of them are. That is fine for the six you know by heart and
 * useless for the rest, and it gets worse as the universe widens.
 *
 * Deliberately NOT fetched from a vendor: these are stable facts about a small
 * fixed list, a network call would make a tooltip flicker, and a description
 * that fails to load is worse than none. Each entry carries what the thing
 * tracks and, where it matters for how the firm trades it, the catch —
 * roll decay, leverage, currency exposure.
 */

export type Instrument = { name: string; what: string; note?: string };

export const INSTRUMENTS: Record<string, Instrument> = {
  // ── Broad market ──────────────────────────────────────────────────────
  SPY: { name: "S&P 500", what: "The 500 largest US companies, weighted by size. The default 'the market'." },
  QQQ: { name: "Nasdaq 100", what: "The 100 biggest non-financial Nasdaq names — tech-heavy, so it moves more than SPY in both directions." },
  IWM: { name: "Russell 2000", what: "US small caps. Reads domestic growth and credit conditions more than the mega-caps do." },
  DIA: { name: "Dow 30", what: "Thirty large US companies weighted by share PRICE, not size — an oddity that makes it behave unlike SPY." },

  // ── Sector SPDRs ──────────────────────────────────────────────────────
  XLE: { name: "Energy", what: "Oil and gas majors, drillers and refiners. Tracks the crude complex more than the broad market." },
  XLF: { name: "Financials", what: "Banks, insurers and asset managers. Sensitive to the yield curve — steeper is usually better for banks." },
  XLK: { name: "Technology", what: "Big tech and semiconductors. Long-duration: it dislikes rising real yields." },
  XLV: { name: "Health Care", what: "Pharma, insurers, devices. Defensive — tends to hold up when the tape sells off." },
  XLI: { name: "Industrials", what: "Machinery, aerospace, rails and freight. A cyclical read on real economic activity." },
  XLY: { name: "Consumer Discretionary", what: "What people buy when they feel well off — retail, autos, travel. Dominated by a couple of huge names." },
  XLP: { name: "Consumer Staples", what: "Food, drink, household goods. The classic defensive hide-out." },
  XLU: { name: "Utilities", what: "Power and water. Traded as a bond proxy — it follows long yields more than earnings." },
  XLB: { name: "Materials", what: "Chemicals, miners, packaging. Moves with the dollar and industrial metals." },
  XLRE: { name: "Real Estate", what: "Listed property (REITs). Highly rate-sensitive; falls when long yields rise." },
  XLC: { name: "Communication Services", what: "Media, telecoms and the big platforms. Concentrated in a handful of names." },

  // ── Industry depth (added 2026-08-28) ─────────────────────────────────
  // Where the dispersion actually lives: XLK is one bet, but SMH vs XLK is a
  // real semis-versus-software choice, and XOP vs XLE separates the drillers
  // from the majors.
  SMH: { name: "Semiconductors", what: "Chipmakers and equipment. Higher beta than XLK and the purest AI-cycle read." },
  XBI: { name: "Biotech", what: "Equal-weighted biotech, so small caps drive it.", note: "Binary drug-trial risk; moves on news, not the tape." },
  XOP: { name: "Oil & Gas Exploration", what: "Drillers and producers, equal-weighted — far more sensitive to crude than XLE's majors." },
  XME: { name: "Metals & Mining", what: "Miners and steel. A cyclical read on industrial metals demand." },
  KRE: { name: "Regional Banks", what: "Smaller US banks.", note: "Very sensitive to the curve and to deposit stress — the 2023 stress epicentre." },
  XRT: { name: "Retail", what: "Equal-weighted retailers — a direct read on consumer spending." },
  ITB: { name: "Homebuilders", what: "Builders and suppliers. Trades almost entirely on mortgage rates." },
  IYT: { name: "Transports", what: "Rails, truckers, parcel. The classic Dow-Theory confirmation of industrial demand." },
  JETS: { name: "Airlines", what: "Passenger airlines.", note: "Leveraged to fuel costs and travel demand at once — the two often fight." },

  // ── Style and breadth ─────────────────────────────────────────────────
  IWF: { name: "Russell 1000 Growth", what: "Large-cap growth — the long-duration half of the market." },
  IWD: { name: "Russell 1000 Value", what: "Large-cap value — cheaper, more cyclical, shorter duration." },
  RSP: { name: "S&P 500 Equal Weight", what: "The same 500 names, each at 0.2%.", note: "RSP versus SPY is the cleanest breadth read there is: if SPY leads, a handful of mega-caps are carrying the index." },

  // ── International ─────────────────────────────────────────────────────
  EFA: { name: "Developed ex-US", what: "Europe, Australasia and the Far East.", note: "Unhedged, so a falling dollar flatters it regardless of local performance." },
  EEM: { name: "Emerging Markets", what: "EM equities, China-heavy.", note: "Historically a dollar-weakness trade as much as a growth one." },
  FXI: { name: "China Large Cap", what: "The largest Chinese companies listed in Hong Kong.", note: "Policy-driven; moves on Beijing, not earnings." },

  // ── Commodities and rates ─────────────────────────────────────────────
  SHY: { name: "1-3 Year Treasuries", what: "Short-dated government bonds — nearly cash, and the front-end read on policy expectations." },
  LQD: { name: "Investment-Grade Credit", what: "High-quality corporate bonds. Carries both rate risk and credit spread risk, unlike TLT which is rates only." },
  GLD: { name: "Gold", what: "Physically backed gold. A hedge against real-rate declines and currency debasement." },
  IAU: { name: "Gold (low fee)", what: "Physically backed gold, same exposure as GLD at a lower expense ratio." },
  SLV: { name: "Silver", what: "Physically backed silver. Part precious metal, part industrial — more volatile than gold." },
  SIVR: { name: "Silver (low fee)", what: "Physically backed silver, cheaper to hold than SLV." },
  USO: { name: "Crude Oil", what: "Tracks oil via FUTURES, not barrels.", note: "Roll decay: in a normal market it bleeds value each month regardless of direction. A short-horizon vehicle only." },
  BNO: { name: "Brent Crude", what: "Brent oil via futures — the international benchmark.", note: "Same roll decay as USO." },
  UNG: { name: "Natural Gas", what: "Natural gas via futures.", note: "Severe roll decay — among the most punishing vehicles to hold." },
  CPER: { name: "Copper", what: "Copper via futures. Read as a proxy for global industrial demand.", note: "Roll decay applies." },
  DBA: { name: "Agriculture", what: "A basket of farm commodities via futures.", note: "Roll decay applies." },
  TLT: { name: "20+ Year Treasuries", what: "Long-dated US government bonds. Rises when long yields fall.", note: "Very rate-sensitive: small yield moves produce large price moves." },
  IEF: { name: "7-10 Year Treasuries", what: "Intermediate US government bonds — the same bet as TLT with roughly half the sensitivity." },
  HYG: { name: "High-Yield Credit", what: "Junk bonds. Watched as a risk-appetite gauge — credit usually cracks before equities do." },
  UUP: { name: "US Dollar Index", what: "The dollar against a basket of major currencies." },
  VIXY: { name: "Short-Term Volatility", what: "VIX futures — a fear gauge.", note: "Decays hard over time; a hedge, never a hold." },

  // ── Single names ──────────────────────────────────────────────────────
  AAPL: { name: "Apple", what: "Consumer hardware and services." },
  MSFT: { name: "Microsoft", what: "Enterprise software and cloud (Azure)." },
  NVDA: { name: "NVIDIA", what: "AI and data-centre chips. The bellwether for the AI trade." },
  AMZN: { name: "Amazon", what: "E-commerce plus AWS cloud." },
  META: { name: "Meta", what: "Facebook, Instagram, WhatsApp — advertising revenue." },
  GOOGL: { name: "Alphabet", what: "Google search, YouTube, cloud — advertising revenue." },
  TSLA: { name: "Tesla", what: "Electric vehicles. Trades on sentiment more than on earnings." },
  AMD: { name: "AMD", what: "CPUs and AI accelerators — NVIDIA's main challenger." },
  NEM: { name: "Newmont", what: "The largest gold miner. Leveraged to the gold price via mining costs." },
  FCX: { name: "Freeport-McMoRan", what: "Copper and gold miner — a leveraged play on industrial metals." },
  GS: { name: "Goldman Sachs", what: "Investment bank." },
  CVX: { name: "Chevron", what: "Integrated oil major." },
  XOM: { name: "Exxon Mobil", what: "Integrated oil major." },
  SLB: { name: "SLB", what: "Oilfield services — leveraged to drilling activity." },
  HON: { name: "Honeywell", what: "Diversified industrial conglomerate." },
  CAT: { name: "Caterpillar", what: "Heavy machinery — a read on construction and mining capex." },
  MRK: { name: "Merck", what: "Pharmaceuticals." },
  LLY: { name: "Eli Lilly", what: "Pharmaceuticals." },
  ORCL: { name: "Oracle", what: "Enterprise databases and cloud." },
  AVGO: { name: "Broadcom", what: "Semiconductors and infrastructure software." },
};

/** FX pairs and crypto are described by rule rather than listed one by one. */
function derived(symbol: string): Instrument | null {
  const s = symbol.toUpperCase();
  if (s.includes("_")) {
    const [base, quote] = s.split("_");
    return {
      name: `${base}/${quote}`,
      what: `Currency pair: how many ${quote} one ${base} buys. Long means betting ${base} strengthens against ${quote}.`,
      note: "Quantities are units of the BASE currency, so P&L accrues in the quote currency.",
    };
  }
  if (s.includes("/")) {
    const [base] = s.split("/");
    const known: Record<string, string> = {
      BTC: "Bitcoin — the largest crypto asset, traded as a macro/liquidity proxy.",
      ETH: "Ether — the second largest, and the settlement asset for most of DeFi.",
      SOL: "Solana — a high-throughput competitor chain. Higher beta than BTC or ETH.",
      LINK: "Chainlink — an oracle network feeding external data to contracts.",
    };
    return { name: base, what: known[base] ?? `${base}, traded against the US dollar.`,
             note: "Trades 24/7, including weekends." };
  }
  // OCC option symbol: root + YYMMDD + C/P + 8-digit strike.
  const opt = /^([A-Z]{1,6})(\d{6})([CP])(\d{8})$/.exec(s);
  if (opt) {
    const [, root, ymd, cp, strike] = opt;
    const exp = `20${ymd.slice(0, 2)}-${ymd.slice(2, 4)}-${ymd.slice(4, 6)}`;
    return {
      name: `${root} ${cp === "C" ? "call" : "put"}`,
      what: `An option on ${root}: the right to ${cp === "C" ? "buy" : "sell"} at $${(
        Number(strike) / 1000).toFixed(2)}, expiring ${exp}.`,
      note: "Loses value as expiry approaches, all else equal.",
    };
  }
  return null;
}

export function describe(symbol: string): Instrument | null {
  if (!symbol) return null;
  return INSTRUMENTS[symbol.toUpperCase()] ?? derived(symbol);
}

/** One-line form for a native `title=` tooltip. */
export function tooltip(symbol: string): string | undefined {
  const d = describe(symbol);
  if (!d) return undefined;
  return `${d.name} — ${d.what}${d.note ? ` (${d.note})` : ""}`;
}
